<template>
  <div>
    <rule-filters
      v-if="enableRuleFilters"
      @emit-filter-refresh="$emit('emit-refresh-render-key')"
      @emit-rule-refresh="refreshRules"
    />
    <div class="group-rounded-box pt-2" :class="$breakpoint.smAndDown ? 'pl-3' : 'pl-5'">
      <span class="title">Rules</span>
      <el-form ref="ruleForm" :model="{ myPricingRules }" :rules="formRules">
        <el-row>
          <el-col :span="23" class="text-right pb-2">
            <button
              class="btn btn-actionable"
              @click.prevent="addRule()"
              v-if="isAddingNewRulesAllowed && !isInOfferModal"
              :disabled="hasPendingRule"
            >
              Add Rule
            </button>
          </el-col>
        </el-row>
        <span class="title" v-if="isInOfferModal">{{
          priority === rulesEnum.ePriority.Global ? "Add Rule" : title
        }}</span>
        <el-row
          class="mimickTable"
          :class="[{ scrollable: !isInOfferModal, minScrollHeight: myPricingRules.length > 5 }]"
        >
          <el-col style="width: 99%">
            <el-row
              :gutter="gridSpacing"
              :class="{ 'mr-4': priority === rulesEnum.ePriority.Offer }"
              class="dark-header-row tableHeaderRow table"
              style="left: 10px"
            >
              <el-col :span="colSpanDefault" class="pl-3"><label class="pt-1">Name</label></el-col>
              <el-col :span="colSpanDefault"><label class="pt-1">UoM</label></el-col>
              <el-col :span="colSpanDefault"><label class="pt-1">Value</label></el-col>
              <template v-if="priority === rulesEnum.ePriority.Global">
                <el-col :span="colSpanOptional" v-if="scope !== bioScope"><label class="pt-1">Product</label></el-col>
                <el-col :span="colSpanOptional"><label class="pt-1">Terminal</label></el-col>
                <el-col :span="colSpanOptional" v-if="scope !== temperatureScope"
                  ><label class="pt-1">Network</label></el-col
                >
                <el-col v-if="scope === customerScope" :span="colSpanOptional"
                  ><label class="pt-1">Customer</label></el-col
                >
              </template>
              <el-col :span="colSpanDefault"></el-col>
            </el-row>
            <el-row
              v-if="myPricingRules.length === 0"
              class="alert tables-alert-custom-message-notify mt-3 mr-5 text-center"
            >
              <i class="fas fa-info-circle mr-2"></i>No rules found
            </el-row>
            <template v-else v-for="(pricingRule, idx) of myPricingRules">
              <el-row v-if="!isRuleHidden(pricingRule)" :key="idx" :class="{ 'pt-3': idx > 0 }">
                <el-row
                  :gutter="gridSpacing"
                  style="width: 100%"
                  class="borderBottom"
                  :class="[
                    { 'highlight-row-t_blue mr-4 pt-3': pricingRule.ruleToOverride },
                    {
                      'highlight-row-alert-t_red mr-4 pt-3':
                        pricingRule.isRemoved || isOverrideAndNullifyingRule(pricingRule),
                    },
                    { 'mr-4 pt-3': priority === rulesEnum.ePriority.Offer },
                    { 'pb-3': hasPendingRule },
                  ]"
                >
                  <!--RULE NAME-->
                  <el-col :span="colSpanDefault" :class="{ 'my-1': !isModifyingRule(pricingRule) }">
                    <el-form-item
                      :prop="`myPricingRules.${idx}.name`"
                      :rules="pricingRule.isRemoved ? {} : formRules.name"
                      v-if="isModifyingRule(pricingRule)"
                    >
                      <el-input
                        v-model="pricingRule.name"
                        placeholder="Enter Rule Name"
                        :disabled="
                          isRowDisabled(pricingRule) || pricingRule.ruleToOverride != null || pricingRule._id != null
                        "
                      ></el-input>
                    </el-form-item>
                    <span v-else class="pl-3">
                      {{ pricingRule.name }}
                    </span>
                  </el-col>
                  <!--UNIT OF MEASUREMENT-->
                  <el-col :span="colSpanDefault" :class="{ 'my-1': !isModifyingRule(pricingRule) }">
                    <el-form-item
                      :prop="`myPricingRules.${idx}.action.unitOfMeasurement`"
                      :rules="pricingRule.isRemoved ? {} : formRules.unitOfMeasurement"
                      v-if="isModifyingRule(pricingRule)"
                    >
                      <el-select
                        v-model="pricingRule.action.unitOfMeasurement"
                        value-key="_id"
                        :disabled="isRowDisabled(pricingRule)"
                        filterable
                        placeholder="Select a UoM"
                        no-match-text="No matching UoM"
                      >
                        <el-option
                          v-for="option in units.filter((x) => x._id !== rulesEnum.eUnitType.Celsius.id)"
                          :key="option._id"
                          :label="option.display"
                          :value="option._id"
                        ></el-option>
                      </el-select>
                    </el-form-item>
                    <span v-else>
                      {{ getDisplayUnit(pricingRule.action.unitOfMeasurement) }}
                    </span>
                  </el-col>
                  <!--VALUE-->
                  <el-col :span="colSpanDefault" :class="{ 'my-1': !isModifyingRule(pricingRule) }">
                    <el-form-item
                      :prop="`myPricingRules.${idx}.action.value`"
                      :rules="pricingRule.isRemoved ? {} : formRules.value"
                      v-if="isModifyingRule(pricingRule)"
                    >
                      <el-input
                        type="text"
                        v-model="pricingRule.action.value"
                        v-cleave="
                          pricingRule.action.unitOfMeasurement === rulesEnum.eUnitType.Celsius.id
                            ? numeralPositive
                            : numeral
                        "
                        :disabled="isRowDisabled(pricingRule)"
                        placeholder="0.00"
                      ></el-input>
                    </el-form-item>
                    <span v-else>
                      {{ pricingRule.action.value | priceformat }}
                    </span>
                  </el-col>
                  <template v-if="priority === rulesEnum.ePriority.Global">
                    <!--PRODUCT-->
                    <el-col
                      :span="colSpanOptional"
                      v-if="scope !== bioScope"
                      :class="{ 'my-1': !isModifyingRule(pricingRule) }"
                    >
                      <el-form-item
                        :prop="`myPricingRules.${idx}.targets[${targetProductIdx}].ids`"
                        :rules="pricingRule.isRemoved ? {} : formRules.product"
                        v-if="isModifyingRule(pricingRule)"
                      >
                        <el-select
                          v-model="pricingRule.targets[targetProductIdx].ids"
                          value-key="_id"
                          multiple
                          filterable
                          collapse-tags
                          @change="onProductChange(pricingRule)"
                          placeholder="Select product(s)"
                        >
                          <el-option class="font-weight-bold" :label="selectAllObject.name" :value="selectAllObject" />
                          <el-option
                            class="font-weight-bold"
                            :label="deselectAllObject.name"
                            :value="deselectAllObject"
                          />
                          <el-option
                            v-for="product in productsList"
                            :key="product._id"
                            :label="product.name"
                            :value="product._id"
                          />
                          <template #empty>No matching UoM</template>
                        </el-select>
                      </el-form-item>
                      <span v-else>
                        {{ getSimplifiedProductDisplayName(pricingRule.targets[targetProductIdx].ids) }}
                      </span>
                    </el-col>
                    <!--TERMINAL-->
                    <el-col :span="colSpanOptional" :class="{ 'my-1': !isModifyingRule(pricingRule) }">
                      <el-form-item
                        :prop="`myPricingRules.${idx}.targets[${targetTerminalIdx}].ids`"
                        :rules="pricingRule.isRemoved ? {} : formRules.terminal"
                        v-if="isModifyingRule(pricingRule)"
                      >
                        <el-select
                          v-model="pricingRule.targets[targetTerminalIdx].ids"
                          value-key="_id"
                          multiple
                          filterable
                          collapse-tags
                          @change="onTerminalChange(pricingRule)"
                          placeholder="Select terminal(s)"
                        >
                          <el-option class="font-weight-bold" :label="selectAllObject.name" :value="selectAllObject" />
                          <el-option
                            class="font-weight-bold"
                            :label="deselectAllObject.name"
                            :value="deselectAllObject"
                          />
                          <el-option
                            v-for="terminal in terminalsList"
                            :key="terminal._id"
                            :label="terminal.name"
                            :value="terminal._id"
                          />
                        </el-select>
                      </el-form-item>
                      <span v-else>
                        {{ getSimplifiedTerminalDisplayName(pricingRule.targets[targetTerminalIdx].ids) }}
                      </span>
                    </el-col>
                    <!--NETWORK-->
                    <el-col
                      :span="colSpanOptional"
                      v-if="scope !== temperatureScope"
                      :class="{ 'my-1': !isModifyingRule(pricingRule) }"
                    >
                      <el-form-item
                        :prop="`myPricingRules.${idx}.targets[${targetNetworkIdx}].ids`"
                        :rules="pricingRule.isRemoved ? {} : formRules.network"
                        v-if="isModifyingRule(pricingRule)"
                      >
                        <el-select
                          v-model="pricingRule.targets[targetNetworkIdx].ids"
                          value-key="_id"
                          multiple
                          filterable
                          collapse-tags
                          @change="onNetworkChange(pricingRule)"
                          placeholder="Select networks(s)"
                          :disabled="!isDieselSelected(pricingRule.targets[targetProductIdx].ids)"
                        >
                          <el-option class="font-weight-bold" :label="selectAllObject.name" :value="selectAllObject" />
                          <el-option
                            class="font-weight-bold"
                            :label="deselectAllObject.name"
                            :value="deselectAllObject"
                          />
                          <el-option
                            v-for="network in networksList"
                            :key="network._id"
                            :label="network.name"
                            :value="network._id"
                          />
                        </el-select>
                      </el-form-item>
                      <span v-else>
                        {{ getSimplifiedNetworkDisplayName(pricingRule.targets[targetNetworkIdx].ids) }}
                      </span>
                    </el-col>
                    <!--CUSTOMER-->
                    <el-col
                      v-if="scope === customerScope"
                      :span="colSpanOptional"
                      :class="{ 'my-1': !isModifyingRule(pricingRule) }"
                    >
                      <el-form-item
                        :prop="`myPricingRules.${idx}.targets[${targetAccountIdx}].ids`"
                        :rules="pricingRule.isRemoved ? {} : formRules.customer"
                        v-if="isModifyingRule(pricingRule)"
                      >
                        <el-select
                          v-model="pricingRule.targets[targetAccountIdx].ids"
                          value-key="_id"
                          multiple
                          filterable
                          collapse-tags
                          @change="onCustomerChange(pricingRule)"
                          placeholder="Select customer(s)"
                        >
                          <el-option class="font-weight-bold" :label="selectAllObject.name" :value="selectAllObject" />
                          <el-option
                            class="font-weight-bold"
                            :label="deselectAllObject.name"
                            :value="deselectAllObject"
                          />
                          <el-option
                            v-for="customer in customersList"
                            :key="customer._id"
                            :label="customer.name"
                            :value="customer._id"
                          />
                        </el-select>
                      </el-form-item>
                      <span v-else>
                        {{ getSimplifiedCustomerDisplayName(pricingRule.targets[targetAccountIdx].ids) }}
                      </span>
                    </el-col>
                  </template>
                  <el-col
                    :span="colSpanDefault"
                    class="d-flex justify-content-start pb-1"
                    v-if="isRulesModificationAllowed"
                  >
                    <template
                      v-if="
                        !(priority === rulesEnum.ePriority.Global) &&
                        !(isRtfoOrDrtfoRule(pricingRule) && priority === rulesEnum.ePriority.Offer && isContractOffer)
                      "
                    >
                      <!-- OVERRIDE RULE -->
                      <button
                        v-if="canRuleBeOverridden(pricingRule) && !pricingRule.isOverridden"
                        :disabled="readOnly"
                        class="btn btn-icon pb-1"
                        @click.prevent="overrideRule(pricingRule, pricingRule.action.value)"
                        title="Override rule"
                      >
                        <i class="fas fa-edit fa-lg" :class="readOnly ? 'text-muted' : 'btn-icon-color'"></i>
                      </button>
                      <!-- REVERT RULE -->
                      <button
                        v-else
                        :disabled="!pricingRule.ruleToOverride"
                        class="btn btn-icon pb-1"
                        @click.prevent="revertRule(pricingRule)"
                        title="Revert rule"
                      >
                        <i
                          class="fas fa-lg fa-globe"
                          :class="[
                            isNonOverrideOfferSpecificRule(pricingRule) ? 'text-muted' : 'btn-icon-color',
                            { 'text-t_red': isOverrideAndNullifyingRule(pricingRule) },
                          ]"
                        ></i>
                      </button>
                    </template>
                    <!-- EDIT RULE -->
                    <button
                      v-if="
                        !pricingRule.isRemoved &&
                        pricingRule._id &&
                        !pendingRule.includes(pricingRule._id) &&
                        !hasPendingRule &&
                        !isInOfferModal
                      "
                      class="btn btn-icon-color mb-4"
                      @click.prevent="editRule(pricingRule)"
                      title="Edit rule"
                    >
                      <i class="fas fa-edit fa-lg"></i>
                    </button>
                    <!-- Save changes button is only available on Global Cost Additions form -->
                    <el-row
                      :gutter="20"
                      v-if="
                        priority === rulesEnum.ePriority.Global &&
                        !isInOfferModal &&
                        (pendingRule.includes(pricingRule._id) || !pricingRule._id)
                      "
                    >
                      <el-col :span="pricingRule._id ? 12 : 20">
                        <button class="btn btn-confirm smallFont" @click.prevent="confirmChanges(pricingRule)">
                          Save
                        </button>
                        <span class="px-1"></span>
                      </el-col>
                      <el-col :span="12" v-if="pricingRule._id" class="pl-2">
                        <button
                          class="btn btn-neutral smallFont"
                          @click.prevent="revertToDisplayRule()"
                          title="Cancel Changes"
                        >
                          Cancel
                        </button>
                      </el-col>
                    </el-row>
                    <!-- REMOVE RULE -->
                    <el-row :gutter="20">
                      <el-col :span="12">
                        <button
                          v-if="
                            !pricingRule.isRemoved &&
                            (!hasPendingRule || !pricingRule._id) &&
                            !(
                              priority === rulesEnum.ePriority.Offer &&
                              isContractOffer &&
                              isRtfoOrDrtfoRule(pricingRule)
                            )
                          "
                          :disabled="!canRuleBeRemoved(pricingRule) || readOnly"
                          class="btn"
                          :class="isInOfferModal || pricingRule._id ? 'btn-icon' : 'btn-neutral'"
                          @click.prevent="removeRule(pricingRule)"
                          title="Remove rule"
                        >
                          <i
                            v-if="isInOfferModal || pricingRule._id"
                            class="fas fa-trash-alt fa-lg"
                            :class="[
                              { 'text-muted': readOnly },
                              { 'pb-2 mb-1': priority === rulesEnum.ePriority.Global },
                            ]"
                          ></i>
                          <span v-else class="smallFont"> Cancel </span>
                        </button>
                      </el-col>
                      <!-- RESTORE RULE -->
                      <el-col :span="12">
                        <button
                          v-if="pricingRule.isRemoved"
                          class="btn btn-icon pb-1"
                          @click.prevent="restoreRule(pricingRule)"
                          title="Restore rule"
                        >
                          <i class="fas fa-lg fa-trash-restore-alt text-t_red"></i>
                        </button>
                      </el-col>
                    </el-row>
                  </el-col>
                  <el-col v-if="pricingRule.ruleToOverride || pricingRule.isRemoved" class="pb-1">
                    <!-- RULE IS AN OVERRIDE -->
                    <template v-if="pricingRule.ruleToOverride">
                      <!-- OVERRIDE IS NULLIFYING THE RULE -->
                      <span v-if="isOverrideAndNullifyingRule(pricingRule)" class="heading-xsmall highlight-row-t_red">
                        <i class="fas fa-exclamation-circle mr-2"></i>This rule has been removed for this offer.<span
                          v-if="isRulesModificationAllowed"
                        >
                          To revert to the global rule value, use the globe icon on the right. Click
                          {{ createOfferOrSaveChangesText }} to confirm.</span
                        >
                      </span>

                      <!-- ANY OTHER OVERRIDE -->
                      <span v-else class="heading-xsmall highlight-row-t_blue">
                        <i class="fas fa-exclamation-circle mr-2"></i>This rule has been adjusted for this offer.<span
                          v-if="isRulesModificationAllowed"
                        >
                          To revert to the global rule value, use the globe icon on the right. Click
                          {{ createOfferOrSaveChangesText }} to confirm.</span
                        >
                      </span>
                    </template>

                    <!-- RULE IS REMOVED -->
                    <span v-else-if="pricingRule.isRemoved" class="highlight-row-t_red heading-xsmall">
                      <i class="fas fa-exclamation-circle mr-2"></i>This rule has been removed from this offer. To
                      re-apply this rule, use the icon on the right. Click {{ createOfferOrSaveChangesText }} to
                      confirm.
                    </span>
                  </el-col>
                  <el-row v-if="Object.keys(savingStatus).length > 0 && isModifyingRule(pricingRule)">
                    <div class="mt-3">
                      <span class="text-nowrap" :class="{ 'text-t_red': !savingStatus.rulesSaved }">{{
                        savingStatus.message
                      }}</span>
                    </div>
                  </el-row>
                </el-row>
              </el-row>
            </template>
          </el-col>
        </el-row>
        <el-row v-if="!isInOfferModal && myPricingRules.length > 0" class="pt-2">
          <el-col :xs="12" :sm="12" :md="12" :lg="12" class="pt-1" :class="{ disabledDiv: hasPendingRule }">
            <paginate
              class="float-left"
              v-model="pageNumber"
              :page-count="getPageNumbers"
              :click-handler="getPaginatedRules"
              :prev-text="'Prev'"
              :next-text="'Next'"
              :container-class="'pagination bg-white'"
              :page-class="'pagination-item'"
              :page-link-class="'border-right-0'"
              :prev-class="'pagination-item pagination-prev rounded-sm rounded-sm-left'"
              :next-class="'pagination-item pagination-next rounded-sm rounded-sm-right'"
              :active-class="'active'"
              :disabled-class="'btn-disabled bg-disabled'"
            >
            </paginate>
          </el-col>
          <el-col
            :xs="12"
            :sm="7"
            :md="8"
            :lg="8"
            :xl="9"
            class="pt-2"
            :class="{ 'mt-1': !$breakpoint.smAndDown }"
            align="right"
            justify="right"
            v-if="!$breakpoint.smAndDown"
          >
            <label class="text-right text-platform-default" v-if="myPricingRules.length > 0">
              {{ viewingRecords }}
            </label>
          </el-col>
          <el-col
            :xs="12"
            :sm="5"
            :md="4"
            :lg="4"
            :xl="3"
            class="pt-1"
            align="right"
            justify="right"
            :class="{ disabledDiv: hasPendingRule }"
          >
            <fieldset class="form-group mt-0 pr-5 text-right" id="__BVID__99">
              <label class="mt-2 mr-2 d-inline-block text-right text-platform-default">Show</label>
              <div tabindex="-1" role="group" class="float-right">
                <div role="radiogroup" tabindex="-1" class="btn-group-toggle btn-group">
                  <label
                    class="btn"
                    :class="{
                      'btn-neutral text-white': pageLimit == 25,
                      'btn-outline-t_greyish-blue text-platform-default bg-white': pageLimit != 25,
                    }"
                  >
                    <input
                      type="radio"
                      name="limit-buttons"
                      autocomplete="off"
                      v-model="pageLimit"
                      value="25"
                      @change="setPageLimit(25)"
                    />
                    <span>25</span>
                  </label>
                  <label
                    class="btn"
                    :class="{
                      'btn-neutral text-white': pageLimit == 30,
                      'btn-outline-t_greyish-blue text-platform-default bg-white': pageLimit != 30,
                    }"
                  >
                    <input
                      type="radio"
                      name="limit-buttons"
                      autocomplete="off"
                      v-model="pageLimit"
                      value="30"
                      @change="setPageLimit(30)"
                    />
                    <span>30</span>
                  </label>
                </div>
              </div>
            </fieldset>
          </el-col>
        </el-row>
      </el-form>
      <!--ADD RULE-->
      <button
        class="btn btn-actionable mb-5 mt-3"
        @click.prevent="addRule()"
        v-if="isAddingNewRulesAllowed && isInOfferModal"
        :disabled="hasPendingRule"
      >
        Add Rule
      </button>
    </div>
    <!-- LIVE OFFERS IMPACT TABLE -->
    <live-offers-impact
      v-if="priority === rulesEnum.ePriority.Global"
      :rules="myPricingRules"
      :scope="scope"
      @setOverridingOfferRulesToDelete="setOverridingOfferRulesToDelete"
    />

    <notification-dialog
      v-if="showNotification"
      :notificationText="notificationText"
      :isModalVisible="showNotification"
      :isError="errorNotification"
      :isConfirmation="confirmationNotification"
      :confirmButtonText="confirmButtonText"
      :notificationBtnLoading="notificationBtnLoading"
      @closeNotification="clearNotificationDialog"
      @confirmedNotification="confirmedChanges"
    />
  </div>
</template>

<script>
const deepEqual = require("fast-deep-equal");
import Cleave from "cleave.js";
import { mapGetters, mapState } from "vuex";
import { cloneDeep, isEqual } from "lodash";

import LiveOffersImpact from "@/components/PricingEngine/LiveOffersImpact.vue";
import RuleFilters from "@/components/PricingEngine/RuleFilters.vue";

import { getRuleScopeOfferExclusions, deleteRules, saveRules, isRtfoOrDrtfoRule } from "@/util/pricingRules";
import { rulesEnum } from "@/util/enums/rules";
import { costAdditionScope } from "@/util/enums/costAdditionScope";
import breakpoint from "@/mixins/index";
import notificationDialog from "@/modals/NotificationDialog.vue";
import { getPricingRules } from "../../api/pricingRules";
import { getOffers } from "../../api/offers";

const cleave = {
  name: "cleave",
  bind(el, binding) {
    const input = el.querySelector("input");
    input._vCleave = new Cleave(input, binding.value);
  },
  unbind(el) {
    const input = el.querySelector("input");
    input._vCleave.destroy();
  },
};

export default {
  directives: { cleave },
  mixins: [breakpoint],
  components: {
    notificationDialog,
    LiveOffersImpact,
    RuleFilters,
  },
  async created() {
    if (this.scope === this.customerScope && this.customerList.length == 0)
      await this.$store.dispatch("accounts/getRelationships");
  },
  data() {
    const validateValue = (rule, value, callback) => {
      const ruleIdx = Number(rule.field.split(".")[1]);
      const { unitOfMeasurement } = this.myPricingRules[ruleIdx].action;
      if (this.isRuleValueInvalid(value, unitOfMeasurement)) {
        callback(new Error("Invalid rule value"));
      }
      callback();
    };

    const validateProduct = (rule, value, callback) => {
      if (value.length === 0) {
        callback(new Error("No product selected"));
      }
      callback();
    };

    const validateTerminal = (rule, value, callback) => {
      if (this.scope === this.temperatureScope) {
        if (value.length === 0) {
          callback(new Error("No terminal selected"));
        }
      } else {
        const ruleIdx = Number(rule.field.split(".")[1]);
        const networkTargetIds = this.myPricingRules[ruleIdx].targets[this.targetNetworkIdx].ids;

        if (value.length === 0 && networkTargetIds.length === 0) {
          callback(new Error("No terminal selected"));
        } else {
          // Clear any error messages on the corresponding network selection if a terminal has been selected
          this.$refs["ruleForm"].clearValidate(`myPricingRules.${ruleIdx}.targets[${this.targetNetworkIdx}].ids`);
        }
      }
      callback();
    };

    const validateNetwork = (rule, value, callback) => {
      const ruleIdx = Number(rule.field.split(".")[1]);
      const terminalTargetIds = this.myPricingRules[ruleIdx].targets[this.targetTerminalIdx].ids;

      if (value.length === 0 && terminalTargetIds.length === 0) {
        callback(new Error("No network selected"));
      } else {
        // Clear any error messages on the corresponding terminal selection if a network has been selected
        this.$refs["ruleForm"].clearValidate(`myPricingRules.${ruleIdx}.targets[${this.targetTerminalIdx}].ids`);
      }
      callback();
    };

    const validateCustomer = (rule, value, callback) => {
      if (value.length === 0) {
        callback(new Error("No customer selected"));
      }
      callback();
    };

    const validateUniqueTemperatureRules = (rule, value, callback) => {
      if (this.scope === this.temperatureScope) {
        const ruleIdx = Number(rule.field.split(".")[1]);
        if (this.isDuplicateTemperatureRule(ruleIdx)) {
          callback(new Error("Temperature rules can only be applied to a single product and terminal combination."));
        } else {
          for (let idx = 0; idx < this.myPricingRules.length; idx++) {
            // Clear any error messages on all other temperature rules if the user has chosen a unique combination
            this.$refs["ruleForm"].clearValidate(`myPricingRules.${idx}.targets[${this.targetTerminalIdx}].ids`);
            this.$refs["ruleForm"].clearValidate(`myPricingRules.${idx}.targets[${this.targetProductIdx}].ids`);
          }
        }
      }
      callback();
    };
    return {
      notificationText: "",
      showNotification: false,
      errorNotification: false,
      confirmationNotification: false,
      confirmButtonText: "",
      notificationBtnLoading: false,
      rulePendingUpdate: null,
      viewingRecords: "0 of 0",
      pageNumber: 1,
      pageLimit: 25,
      pendingRule: [],
      isRevertingRule: false,
      ruleForm: {
        pricingRules: [],
      },
      originalRuleValues: {},
      clearSavingStatusTimeout: 0,
      savingStatus: {},
      formRules: {
        name: [{ required: true, message: "Please enter a name", trigger: "blur" }],
        unitOfMeasurement: [{ required: true, message: "No UoM selected", trigger: "change" }],
        value: [{ required: true, validator: validateValue, trigger: "blur" }],
        product: [
          { required: true, validator: validateUniqueTemperatureRules },
          { required: true, validator: validateProduct, trigger: "change" },
        ],
        terminal: [
          { required: true, validator: validateUniqueTemperatureRules },
          { required: true, validator: validateTerminal, trigger: "change" },
        ],
        network: [{ required: true, validator: validateNetwork, trigger: "change" }],
        customer: [{ required: true, validator: validateCustomer, trigger: "change" }],
      },
      rulesEnum,
      eExclusionAction: {
        Add: "ADD",
        Remove: "REMOVE",
      },
      selectAllObject: {
        _id: "SelectAll",
        id: "SelectAll",
        name: "Select All",
      },
      deselectAllObject: {
        _id: "DeselectAll",
        id: "DeselectAll",
        name: "Deselect All",
      },
      costAdditionScopeGetter: costAdditionScope,
      isRtfoOrDrtfoRule,
    };
  },
  props: {
    priority: {
      type: String,
      required: true,
    },
    pricingRules: {
      type: Array,
      required: true,
    },
    scope: {
      type: String,
      required: true,
    },
    isRulesModificationAllowed: {
      type: Boolean,
      default: true,
    },
    title: {
      type: String,
      default: "",
    },
    isInOfferModal: {
      type: Boolean,
      default: false,
    },
    originalPricingRulesList: {
      type: Array,
      default: () => [],
      required: true,
    },
    pendingModificationRuleId: {
      type: Object,
      default: () => {},
    },
    enableRuleFilters: {
      type: Boolean,
      default: false,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
    isContractOffer: {
      type: Boolean,
      default: false,
    },
  },
  beforeMount() {
    this.$emit("update:pendingModificationRuleId", {});
  },
  computed: {
    ...mapState("inputCheckers", ["numeralPositive", "numeral"]),
    ...mapGetters("rules", ["ruleValidationThreshold", "isRuleRemovalConfirmed"]),
    ...mapGetters("fuelsType", ["getFuelsType", "getSpotFuels"]),
    ...mapGetters("terminals", ["getTerminalChanges"]),
    ...mapGetters("accounts", ["customerList"]),
    ...mapGetters("metadata", ["productConfig", "networks"]),
    ...mapGetters("itemRemoval", ["unsavedChangesRemovalConfirmed"]),
    getPageNumbers() {
      const totalCount = this.$store.getters["rules/getPageMetadata"].total
        ? this.$store.getters["rules/getPageMetadata"].total
        : 0;
      return Math.ceil(totalCount / this.pageLimit);
    },
    myPricingRules() {
      return this.pricingRules;
    },
    hasModifiableRules() {
      return this.myPricingRules !== null;
    },
    hasPendingRule() {
      return this.pendingRule.length > 0;
    },
    targetProductIdx: function () {
      return this.myPricingRules.length > 0
        ? this.myPricingRules[0].targets.findIndex((t) => t.type === this.rulesEnum.eTargetType.Product)
        : -1;
    },
    targetTerminalIdx: function () {
      return this.myPricingRules.length > 0
        ? this.myPricingRules[0].targets.findIndex((t) => t.type === this.rulesEnum.eTargetType.Terminal)
        : -1;
    },
    targetNetworkIdx: function () {
      return this.myPricingRules.length > 0
        ? this.myPricingRules[0].targets.findIndex((t) => t.type === this.rulesEnum.eTargetType.Network)
        : -1;
    },
    targetAccountIdx: function () {
      return this.myPricingRules.length > 0
        ? this.myPricingRules[0].targets.findIndex((t) => t.type === this.rulesEnum.eTargetType.Account)
        : -1;
    },
    bioScope: function () {
      return this.costAdditionScopeGetter.Bio;
    },
    temperatureScope: function () {
      return this.costAdditionScopeGetter.Temperature;
    },
    customerScope: function () {
      return this.costAdditionScopeGetter.Customer;
    },
    productsList: function () {
      let products = [];

      if (this.scope === this.bioScope) {
        // Only return Biodiesel if this is a Bio rules tab
        const { _id, name } = this.getFuelsType.find((f) => f._id === this.productConfig.BioDieselId);
        products.push({ _id, name });
      }
      // Return all spot products
      else {
        products.push(...this.getSpotFuels.map(({ _id, name }) => ({ _id, name })));
      }
      return products;
    },
    terminalsList: function () {
      return this.getTerminalChanges.map(({ _id, name }) => ({ _id, name }));
    },
    networksList: function () {
      return this.networks.map(({ _id, name }) => ({ _id, name }));
    },
    customersList: function () {
      return this.customerList.map(({ id, name }) => ({ _id: id, name }));
    },
    gridSpacing: function () {
      return this.priority === this.rulesEnum.ePriority.Global ? 15 : 25;
    },
    colSpanDefault: function () {
      if (this.priority === this.rulesEnum.ePriority.Global) {
        switch (this.scope) {
          case this.customerScope:
            return 3;
          case this.bioScope:
          case this.temperatureScope:
            return 4;
          default:
            return 3;
        }
      }
      if (!this.isRulesModificationAllowed) {
        return 8;
      }
      return 6;
    },
    colSpanOptional: function () {
      if (this.priority === this.rulesEnum.ePriority.Global) {
        switch (this.scope) {
          case this.customerScope:
            return 3;
          case this.bioScope:
          case this.temperatureScope:
            return 4;
          default:
            return 4;
        }
      }
      if (!this.isRulesModificationAllowed) {
        return 4;
      }
      return 6;
    },
    units: function () {
      let unitsToShow = [rulesEnum.eUnitType.PencePerLitre, rulesEnum.eUnitType.UsdPerMetricTonne];
      if (this.scope === this.temperatureScope) {
        unitsToShow.push(rulesEnum.eUnitType.Celsius);
      }
      return unitsToShow.map((uom) => ({ _id: uom.id, display: uom.display }));
    },
    isAddingNewRulesAllowed: function () {
      return this.isRulesModificationAllowed && !this.readOnly;
    },
    isFormComplete: function () {
      // Check if there is a rule without a name
      if (this.myPricingRules.find((rule) => !this.isRowDisabled(rule) && rule.name === "")) return false;

      // Check if there is a rule without a unit of measurement selected
      if (this.myPricingRules.find((rule) => !this.isRowDisabled(rule) && !rule.action.unitOfMeasurement)) return false;

      // Check if there is a rule with an invalid value
      if (
        this.myPricingRules.find(
          (rule) =>
            !this.isRowDisabled(rule) && this.isRuleValueInvalid(rule.action.value, rule.action.unitOfMeasurement)
        )
      )
        return false;

      // Check if there is a rule without any product selected
      if (
        this.myPricingRules.find(
          (rule) =>
            this.priority !== this.rulesEnum.ePriority.Offer && rule.targets[this.targetProductIdx].ids.length == 0
        )
      )
        return false;

      // Check if there is a rule without any terminal/network selected
      if (
        this.myPricingRules.find(
          (rule) =>
            this.priority !== this.rulesEnum.ePriority.Offer &&
            rule.targets[this.targetTerminalIdx].ids.length == 0 &&
            (this.scope === this.temperatureScope || rule.targets[this.targetNetworkIdx].ids.length == 0)
        )
      )
        return false;
      // Disable button if there are no tracked changes in the dataset
      if (this.priority === this.rulesEnum.ePriority.Global && this.haveRulesChanged) return false;
      return true;
    },
    haveRulesChanged: function () {
      return deepEqual(this.originalPricingRulesList, this.myPricingRules) && !this.hasPendingRule;
    },
    createOfferOrSaveChangesText: function () {
      return this.getEditingStatus ? "Save Changes" : "Create Offer";
    },
  },
  watch: {
    myPricingRules: {
      handler() {
        this.viewingRecords = `${(this.pageNumber - 1) * this.pageLimit + 1} - ${
          (this.pageNumber - 1) * this.pageLimit + this.originalPricingRulesList.length
        } of ${this.$store.getters["rules/getPageMetadata"].total}`;
      },
      deep: true,
    },
    isFormComplete: {
      handler() {
        // Set rules validity to offer popup, so we can disable saving until all rules are valid
        if (this.priority === this.rulesEnum.ePriority.Offer) {
          this.$emit("setRulesValidity", this.scope, this.isFormComplete);
        }
        if (this.priority === this.rulesEnum.ePriority.Global) {
          this.$emit("setChangesDetected", this.scope, !this.haveRulesChanged);
        }
      },
      deep: true,
    },
    unsavedChangesRemovalConfirmed() {
      if (this.unsavedChangesRemovalConfirmed && this.isRevertingRule) {
        this.revertRuleConfirm();
      }
    },
    isRuleRemovalConfirmed: function () {
      if (this.isRuleRemovalConfirmed && this.$store.getters["rules/deletedRuleScope"] === this.scope)
        this.removeRule(this.$store.getters["itemRemoval/itemToRemove"], true);
    },
  },
  methods: {
    getDisplayUnit(unit) {
      return this.units.find((x) => x._id === unit).display;
    },
    setPageLimit(limit) {
      this.pageLimit = limit;
      this.getPaginatedRules(1);
    },
    getPaginatedRules(pageNumber) {
      if (pageNumber && pageNumber > 0) {
        this.pageNumber = pageNumber;
      }
      const payload = { pageType: this.scope, pageLimit: this.pageLimit, pageNumber: this.pageNumber };
      this.$emit("newLimitOrPageSelected", payload);
    },
    isEditingRule(row) {
      return row && row._id && this.pendingRule.includes(row._id);
    },
    isAddingRule(row) {
      return row && !row._id;
    },
    isModifyingRule(row) {
      if (this.isInOfferModal) {
        return true;
      } else if (this.isEditingRule(row) || this.isAddingRule(row)) {
        return true;
      }
    },
    getSimplifiedProductDisplayName(products) {
      let displayName = this.productsList.find((x) => x._id === products[0]);
      if (products.length > 1 && displayName) {
        return `${displayName.name},  +${products.length - 1}`;
      } else if (products.length === 1 && displayName) {
        return `${displayName.name}`;
      } else {
        return "";
      }
    },
    getSimplifiedTerminalDisplayName(terminals) {
      let displayName = this.terminalsList.find((x) => x._id === terminals[0]);
      if (terminals.length > 1 && displayName) {
        return `${displayName.name},  +${terminals.length - 1}`;
      } else if (terminals.length === 1 && displayName) {
        return `${displayName.name}`;
      } else {
        return "";
      }
    },
    getSimplifiedNetworkDisplayName(networks) {
      let displayName = this.networksList.find((x) => x._id === networks[0]);
      if (networks.length > 1 && displayName) {
        return `${displayName.name},  +${networks.length - 1}`;
      } else if (networks.length === 1 && displayName) {
        return `${displayName.name}`;
      } else {
        return "";
      }
    },
    getSimplifiedCustomerDisplayName(customers) {
      let displayName = this.customersList.find((x) => x._id === customers[0]);
      if (customers.length > 1 && displayName) {
        return `${displayName.name},  +${customers.length - 1}`;
      } else if (customers.length === 1 && displayName) {
        return `${displayName.name}`;
      } else {
        return "";
      }
    },
    addRule: function (ruleToOverride, ruleValue) {
      // Setup a new empty rule or assign the relevant details from original rule if we are overriding it
      let newRule = {
        name: ruleToOverride ? ruleToOverride.name : "",
        type: this.rulesEnum.eType.Cumulative,
        priority: this.priority,
        targets: ruleToOverride ? ruleToOverride.targets : [],
        action: ruleToOverride
          ? ruleToOverride.action
          : {
              targetAttribute:
                this.scope !== this.bioScope
                  ? this.rulesEnum.eTargetAttributeType.Price
                  : rulesEnum.eTargetAttributeType.BioBlend,
              value: null,
              unitOfMeasurement: null,
            },
        offerExclusions: ruleToOverride ? ruleToOverride.offerExclusions : [],
      };

      if (ruleToOverride) {
        newRule.ruleToOverride = ruleToOverride._id;
      } else {
        // If this a new rule without overriding, then assign the rest of the details
        switch (this.priority) {
          case this.rulesEnum.ePriority.Global: // Add a list of products, terminals, and networks for global cost additions
            if (this.scope === this.bioScope) {
              newRule.targets.push({ type: this.rulesEnum.eTargetType.Product, ids: [this.productConfig.BioDieselId] });
            } else {
              newRule.targets.push({ type: this.rulesEnum.eTargetType.Product, ids: [] });
            }
            newRule.targets.push({
              type: this.rulesEnum.eTargetType.Terminal,
              ids: [],
            });
            if (this.scope !== this.temperatureScope)
              newRule.targets.push({ type: this.rulesEnum.eTargetType.Network, ids: [] });
            break;
          case this.rulesEnum.ePriority.Offer:
            newRule.isOverridden = false;
            newRule.isRemoved = false;
            break;
        }

        newRule.offerExclusions = getRuleScopeOfferExclusions(
          this.scope,
          this.costAdditionScopeGetter,
          this.rulesEnum.eOfferExclusion,
          isRtfoOrDrtfoRule(newRule.name)
        );
        switch (this.scope) {
          case this.bioScope: // If this is an offer specific rule, add Biodiesel as a target
            if (this.priority === this.rulesEnum.ePriority.Offer)
              newRule.targets.push({ type: this.rulesEnum.eTargetType.Product, ids: [this.productConfig.BioDieselId] });
            break;
          case this.customerScope: // Add a list of customers if this is customer margins section
            newRule.targets.push({ type: this.rulesEnum.eTargetType.Account, ids: [] });
            break;
        }
      }

      // Assign rule value if specified
      if (typeof ruleValue === "number")
        newRule.action.value = Number(ruleValue.toString().replace(/,/g, "")).toFixed(2);

      if (ruleToOverride) {
        const ruleIdx = this.myPricingRules.findIndex((rule) => rule._id === ruleToOverride._id);
        this.myPricingRules.splice(ruleIdx + 1, 0, newRule);
      } else {
        if (this.isInOfferModal) {
          // offer modal has add rule at the bottom
          this.myPricingRules.push(newRule);
        } else {
          // else add rule is located on top of the component
          // new rule has no id so we jsut send empty string for now
          this.$emit("update:pendingModificationRuleId", { _id: "" });
          this.myPricingRules.unshift(newRule);
        }
      }
      if (!this.isInOfferModal) {
        this.pendingRule = [this.myPricingRules.length - 1];
        this.savingStatus = {};
      }
    },
    revertRule: function (rule) {
      rule.isRemoved = true;
      // Remove the overridden rule from exclusion list
      const overriddenRuleIdx = this.myPricingRules.findIndex((r) => r._id === rule.ruleToOverride);
      const orignalRuleData = this.originalPricingRulesList.find(
        (r) => r._id === this.myPricingRules[overriddenRuleIdx]._id
      );
      this.$set(this.myPricingRules[overriddenRuleIdx], "isOverridden", false);
      this.$set(this.myPricingRules[overriddenRuleIdx], "action", orignalRuleData.action);
    },
    restoreRule: function (rule) {
      rule.isRemoved = false;
    },
    editRule(rule) {
      if (!this.pendingRule.includes(rule._id)) {
        this.savingStatus = {};
        this.pendingRule.push(rule._id);
        this.originalRuleValues = cloneDeep(rule);
        this.$emit("update:pendingModificationRuleId", { _id: this.originalRuleValues._id });
      }
    },
    revertToDisplayRule() {
      const originalRuleIndex = this.originalPricingRulesList.findIndex((r) => r._id == this.pendingRule[0]);
      if (!isEqual(this.originalRuleValues, this.myPricingRules[originalRuleIndex])) {
        this.isRevertingRule = true;
        this.$emit("showRemovalConfirmation", true);
      } else {
        this.revertRuleConfirm();
      }
    },
    revertRuleConfirm() {
      const originalRuleIndex = this.originalPricingRulesList.findIndex((r) => r._id == this.pendingRule[0]);
      this.myPricingRules[originalRuleIndex] = this.originalRuleValues;
      this.originalRuleValues = null;
      this.pendingRule = [];
      this.$store.dispatch("itemRemoval/confirmUnsavedChangesRemoval", false);
      this.isRevertingRule = false;
      this.$emit("setChangesDetected", this.scope, false);
      this.$emit("update:pendingModificationRuleId", {});
    },
    removeRule: async function (rule, isActionConfirmed) {
      const { ePriority } = this.rulesEnum;

      if (this.priority === ePriority.Offer) {
        if (rule.priority === ePriority.Global) {
          // Removing a global rule is equivalent to overriding its value to 0
          this.overrideRule(rule, 0);
        } else {
          rule.isRemoved = true;
        }
      } else if (!isActionConfirmed) {
        this.$store.dispatch("rules/setDeletedRuleScope", this.scope);

        // Show removal confirmation modal
        const payload = {
          item: rule,
          type: this.getItemRemovalType(),
          showWindow: true,
        };

        this.$store.dispatch("itemRemoval/openConfirmationRemoval", payload);
        setTimeout(() => {
          const removalModal = this.$root.$refs.confirmationRemoveItem.$el;
          $(removalModal).modal({ backdrop: "static", keyboard: false });
        }, 500);
      } else {
        try {
          this.$store.dispatch("rules/confirmRuleRemoval", false); // Reset latch used in the watcher

          if (rule._id) {
            const rulesToDelete = [rule];

            // Check if there are any linked overriding rules to delete
            const { rules: rulesOverridingThisRule } = await getPricingRules({
              pageLimit: 0,
              ruleIdsToOverride: [rule._id],
            });
            if (rulesOverridingThisRule.length > 0) {
              // Grab the list of offer IDs specified in the overriding rules
              const offerIdsWithOverridingRules = [
                ...new Set(
                  rulesOverridingThisRule.flatMap((r) => {
                    const offerTarget = r.targets.find((t) => t.type === this.rulesEnum.eTargetType.Offer);
                    // The rule does not target an offer, so we can ignore this rule
                    if (!offerTarget) {
                      return null;
                    }
                    return offerTarget.ids;
                  })
                ),
              ];
              if (offerIdsWithOverridingRules.length > 0) {
                // Fetch specified offers to check which offers are contracts
                const { offers: offersWithOverridingRules } = await getOffers({
                  pageLimit: offerIdsWithOverridingRules.length,
                  offerIds: offerIdsWithOverridingRules,
                });
                // Only delete overriding rules for non-contract offers
                const nonContractOfferIds = offersWithOverridingRules
                  .filter((offer) => !offer.isContract)
                  .map((offer) => offer._id);
                const overridingRulesForNonContractOffers = rulesOverridingThisRule.filter((r) => {
                  const offerTarget = r.targets.find((t) => t.type === this.rulesEnum.eTargetType.Offer);
                  if (!offerTarget) {
                    return false;
                  }
                  return offerTarget.ids.some((id) => nonContractOfferIds.includes(id));
                });
                if (overridingRulesForNonContractOffers.length > 0) {
                  // Add non-contract offer overriding rules to the deletion list
                  rulesToDelete.push(...overridingRulesForNonContractOffers);
                }
              }
            }
            await deleteRules(rulesToDelete);

            // Fetch updated list of rules and offers
            if (this.priority === this.rulesEnum.ePriority.Global) {
              this.$emit("updateCostAdditionRulesPricingEngine", {
                pageType: this.scope,
                pageLimit: this.pageLimit,
                pageNumber: this.pageNumber,
              });
            }

            this.$store.dispatch("sellerOffers/getAllOffersForUser");
          }

          // Remove rule from the component
          const ruleIndex = this.myPricingRules.findIndex((r) => r == rule);
          this.myPricingRules.splice(ruleIndex, 1);

          this.$emit("update:pendingModificationRuleId", {});

          // Hide removal confirmation modal and show parent modal again
          this.$store.dispatch("itemRemoval/openConfirmationRemoval", false);

          this.$store.dispatch("rules/setDeletedRuleScope", null);

          this.pendingRule = [];
        } catch (error) {
          console.dir(error); // @TO-DO: Handle error when trying to remove a rule
        }
      }
    },
    overrideRule: function (rule, ruleValue = null) {
      // Check first whether this rule already has an override or not, if so we re-use
      const overridingRuleIdx = this.myPricingRules.findIndex((r) => r.ruleToOverride === rule._id);
      if (overridingRuleIdx > -1) {
        // Assign rule value if specified
        const override = this.myPricingRules[overridingRuleIdx];
        if (typeof ruleValue === "number") {
          override.action.value = Number(ruleValue).toFixed(2);
        } else if (typeof ruleValue === "string") {
          override.action.value = Number(ruleValue.toString().replace(/,/g, "")).toFixed(2);
        }
        override.isRemoved = false;
      } else {
        const ruleDeepCopy = rule;
        this.addRule(ruleDeepCopy, ruleValue); // Create a new rule using the original rule's details
      }
      rule.isOverridden = true;
    },
    getItemRemovalType: function () {
      const enumSelection = this.$store.getters["itemRemoval/eRemoveItemType"];

      switch (this.priority) {
        case this.rulesEnum.ePriority.Global:
          return enumSelection.Rule;
        case this.rulesEnum.ePriority.Offer:
          return enumSelection.Rule;
      }
    },
    isRuleHidden: function (rule) {
      return rule.isOverridden || (rule.ruleToOverride && rule.isRemoved);
    },
    canRuleBeOverridden: function (rule) {
      return this.priority !== this.rulesEnum.ePriority.Global && rule.priority === this.rulesEnum.ePriority.Global;
    },
    canRuleBeRemoved: function (rule) {
      /**
       * CONDITION:
       * Rule is set to global priority OR rule is not an override)
       */
      const { ePriority } = this.rulesEnum;
      return rule.priority === ePriority.Global || !rule.ruleToOverride;
    },
    isRuleValueInvalid: function (value, unitOfMeasurement) {
      value = Number(value);
      const isTemperatureRuleInCelsius =
        this.scope === this.temperatureScope && unitOfMeasurement === this.rulesEnum.eUnitType.Celsius.id;

      if (isTemperatureRuleInCelsius) {
        // If it is a temperature cost addition and the unit selected is Celsius
        // Check whether value is an integer between min and max temperatures set
        return (
          !Number.isInteger(value) ||
          value < this.ruleValidationThreshold.MinTemperatureCelsius ||
          value > this.ruleValidationThreshold.MaxTemperatureCelsius
        );
      } else {
        return false;
      }
    },
    isDuplicateTemperatureRule: function (ruleIdx) {
      const ruleToCheck = this.myPricingRules[ruleIdx];
      const rulesToCheckAgainst = this.myPricingRules.filter((rule, idx) => idx !== ruleIdx);
      for (const rule of rulesToCheckAgainst) {
        const terminalIds = rule.targets[this.targetTerminalIdx].ids.map((id) => id);
        const productIds = rule.targets[this.targetProductIdx].ids.map((id) => id);

        // Check if there are other temperature rules with the same terminal and product combination
        const isDuplicate =
          ruleToCheck.targets[this.targetTerminalIdx].ids.some((id) => terminalIds.includes(id)) &&
          ruleToCheck.targets[this.targetProductIdx].ids.some((id) => productIds.includes(id));
        if (isDuplicate) {
          return true;
        }
      }
      return false;
    },
    isOverrideAndNullifyingRule: function (rule) {
      /**
       * CONDITION:
       * Rule is an override
       * AND rule value is 0
       */
      return rule.ruleToOverride && Number(rule.action.value) == 0;
    },
    isNonOverrideOfferSpecificRule: function (rule) {
      /**
       * CONDITION:
       * Rule priority is set to offer priority
       * AND rule is not an override
       */
      const { ePriority } = this.rulesEnum;
      return rule.priority === ePriority.Offer && !rule.ruleToOverride;
    },
    isRowDisabled: function (rule) {
      return (
        (this.canRuleBeOverridden(rule) && !rule.isOverridden) || rule.isRemoved || !this.isRulesModificationAllowed
      );
    },
    setOverridingOfferRulesToDelete: function (overridingOfferRulesToDelete) {
      this.overridingOfferRulesToDelete = overridingOfferRulesToDelete;
    },
    validateRules: async function () {
      return new Promise((res) => {
        this.$refs["ruleForm"].validate((valid) => {
          return res(valid);
        });
      });
    },
    fetchUpdatedRulesAndOffersList: function () {
      this.pendingRule = [];
      if (!this.isInOfferModal) {
        this.$emit("update:pendingModificationRuleId", {});
        this.$emit("updateCostAdditionRulesPricingEngine", {
          pageType: this.scope,
          pageLimit: this.pageLimit,
          pageNumber: this.pageNumber,
        });
      }
      this.$store.dispatch("sellerOffers/getAllOffersForUser");
    },
    confirmedChanges: async function () {
      this.notificationBtnLoading = true;
      const shouldCheckUpdateRecency = false;
      await saveRules([this.rulePendingUpdate], true, "costAdditions", shouldCheckUpdateRecency);
      this.clearNotificationDialog();
    },
    showNotificationDialog(isError) {
      if (isError) {
        this.notificationText = "This rule has been deleted. <br/> Please close this window to view active rules. ";
        this.errorNotification = true;
        this.confirmationNotification = false;
        this.showNotification = true;
      } else {
        this.notificationText =
          "This rule has been updated by another user. <br/> You can either save your change or go back to view the updated rule.";
        this.errorNotification = false;
        this.confirmationNotification = true;
        this.confirmButtonText = "Save Changes";
        this.notificationBtnLoading = false;
        this.showNotification = true;
      }
    },
    clearNotificationDialog() {
      this.notificationText = "";
      this.showNotification = false;
      this.errorNotification = false;
      this.confirmationNotification = false;
      this.confirmButtonText = "";
      this.notificationBtnLoading = false;
      this.rulePendingUpdate = null;
      this.fetchUpdatedRulesAndOffersList();
    },
    confirmChanges: async function (rule) {
      const rulesAreValid = await this.validateRules();
      if (rulesAreValid) {
        try {
          // we need to convert the string value into a number and reassign before saving
          rule.action.value = Number(rule.action.value.toString().replace(/,/g, "")).toFixed(2);
          let shouldCheckUpdateRecency = false;
          if (this.isEditingRule(rule)) {
            shouldCheckUpdateRecency = true;
          }
          const updateResponse = await saveRules([rule], true, "costAdditions", shouldCheckUpdateRecency);
          // ask for confirmation of change if rule has recently been updated
          if (updateResponse && updateResponse.hasRecentUpdate) {
            this.rulePendingUpdate = rule;
            this.showNotificationDialog();
          } else if (updateResponse && updateResponse.hasDeletedRule) {
            this.showNotificationDialog(true);
          } else {
            // Delete a list of overriding offer-specific rules that will be redundant after rule changes
            // IMPORTANT: Deletion should only be done if saving rules above is successful
            await deleteRules(this.overridingOfferRulesToDelete);
            this.savingStatus = { rulesSaved: true, message: "Rules saved" };
            this.fetchUpdatedRulesAndOffersList();
          }
        } catch (ex) {
          let errorMessage = "Unable to save rules. Please try again later.";

          const errorData = ex.response && ex.response.data;
          if (errorData && errorData.code === "GLOBAL_TEMPERATURE_RULE_DUPLICATED") {
            errorMessage = "Temperature rules can only be applied to a single product and terminal combination";
          }

          this.savingStatus = { rulesSaved: false, message: errorMessage }; // Flag to users that request failed
        } finally {
          // Clear saving status after few seconds
          this.clearSavingStatusTimeout = setTimeout(() => {
            this.savingStatus = {};
          }, 300);
        }
      }
    },
    onProductChange(rule) {
      const selectedProductIds = rule.targets[this.targetProductIdx].ids.map(({ _id }) => _id);
      if (selectedProductIds.includes(this.selectAllObject._id)) {
        rule.targets[this.targetProductIdx].ids = this.productsList.map(({ _id }) => _id);
      } else if (selectedProductIds.includes(this.deselectAllObject._id)) {
        rule.targets[this.targetProductIdx].ids = [];
      } else {
        this.removeNetworkFromRuleWithoutDiesel(rule);
      }
    },
    onTerminalChange(rule) {
      const selectedTerminalIds = rule.targets[this.targetTerminalIdx].ids.map(({ _id }) => _id);
      if (selectedTerminalIds.includes(this.selectAllObject._id)) {
        rule.targets[this.targetTerminalIdx].ids = this.terminalsList.map(({ _id }) => _id);
      } else if (selectedTerminalIds.includes(this.deselectAllObject._id)) {
        rule.targets[this.targetTerminalIdx].ids = [];
      }
    },
    onNetworkChange(rule) {
      const selectedNetworkIds = rule.targets[this.targetNetworkIdx].ids.map(({ _id }) => _id);
      if (selectedNetworkIds.includes(this.selectAllObject._id)) {
        rule.targets[this.targetNetworkIdx].ids = this.networksList.map(({ _id }) => _id);
      } else if (selectedNetworkIds.includes(this.deselectAllObject._id)) {
        rule.targets[this.targetNetworkIdx].ids = [];
      }
    },
    onCustomerChange(rule) {
      const selectedCustomerIds = rule.targets[this.targetAccountIdx].ids.map(({ _id }) => _id);
      if (selectedCustomerIds.includes(this.selectAllObject._id)) {
        rule.targets[this.targetAccountIdx].ids = this.customersList.map(({ _id }) => _id);
      } else if (selectedCustomerIds.includes(this.deselectAllObject._id)) {
        rule.targets[this.targetAccountIdx].ids = [];
      }
    },
    clearTerminalValidation(rule) {
      return rule.targets[this.targetNetworkIdx].ids.length > 0;
    },
    removeNetworkFromRuleWithoutDiesel(rule) {
      if (
        this.scope !== this.bioScope && // We can keep network options if this is a Bio product rule
        this.scope !== this.temperatureScope && // Or do nothing if it is a temperature cost addition, since no networks are targeted
        this.priority === this.rulesEnum.ePriority.Global
      ) {
        // Check whether this rule includes Diesel in its selected product or not
        const productIds = rule.targets[this.targetProductIdx].ids;
        if (!this.isDieselSelected(productIds)) {
          // Remove selection of networks
          rule.targets[this.targetNetworkIdx].ids = [];
        }
      }
    },
    isDieselSelected(productList) {
      // Map the list of selected products to get the list of selected product IDs, then check whether Diesel/Biodiesel is selected or not
      const productIdsList = productList.map((p) => p._id || p);
      const dieselBasedProductIds = [this.productConfig.DieselId, this.productConfig.BioDieselId];
      return productIdsList.some((id) => dieselBasedProductIds.includes(id));
    },
    refreshRules() {
      this.$emit("updateCostAdditionRulesPricingEngine", {
        pageType: this.scope,
        pageLimit: this.pageLimit,
        pageNumber: this.pageNumber,
      });
    },
  },
  beforeDestroy() {
    clearTimeout(this.clearSavingStatusTimeout);
    this.savingStatus = {};
  },
};
</script>

<style lang="scss" scoped>
::v-deep .el-select {
  width: 100%;
}
::v-deep .el-form-item__content {
  line-height: 25px !important;
}

::v-deep .mimickTable {
  font-size: 12px;
}

::v-deep .tableHeaderRow {
  .col {
    padding: 0.3075rem 0.615rem;
  }
}

.borderBottom {
  border-bottom: 1px solid map-get($platform-theme, tabs-borders-colour);
}

.smallFont {
  font-size: 12px;
}

::v-deep .scrollable {
  max-height: 700px;
  overflow: scroll;
}

::v-deep .minScrollHeight {
  min-height: 400px;
}

.disabledDiv {
  pointer-events: none;
  opacity: 0.4;
}
</style>
